//@flow
import React from 'react';
import MediaQuery from 'react-responsive';
import FilterDropdown from '../../Filters/components/FilterDropdown.react';
import FilterPills from '../../Filters/components/FilterPills.react';
import Query from '../../Utils/query';
import CategoriesSlider from './CategoriesSlider.react';
import MsrpLearnMoreLink from '../../Cart/components/MsrpLearnMoreLink.react';
import UrlData from '../../Utils/url-data';
import {withRouter} from 'react-router';

import type { List } from 'immutable';
import type ProductsCollection from '../../Stores/Products/productsCollection';
import type StockCollection from '../../Stores/Stock/stockCollection';
import type QuoteModel from '../../Stores/Quote/quoteModel';
import type QuoteItemsCollection from '../../Stores/Quote/quoteItemsCollection';
import type QuoteTotalsModel from '../../Stores/Quote/quoteTotalsModel';
import type MembershipPlanModel from '../../Stores/Membership/membershipPlanModel';
import type ProductModel from '../../Stores/Products/productModel';
import type CategoriesCollection from '../../Stores/MemberCategories/categoriesCollection';
import type CategoryModel from '../../Stores/MemberCategories/categoryModel';
import type CustomerModel from '../../Stores/Customer/customerModel';
import type { Filter, FilterCategories } from '../../Stores/Filters/filtersStoreClass';
import type {ContextRouter} from 'react-router';
import DelightedTestimonials from './DelightedTestimonials.react';

type Props = ContextRouter&{
  allFilters:FilterCategories,
  filters:Filter[],
  filteredProducts:ProductModel[],
  products:ProductsCollection,
  stock?:StockCollection,
  quote:QuoteModel,
  quoteItems:QuoteItemsCollection,
  quoteTotals:QuoteTotalsModel,
  isMember:boolean,
  inUS:boolean,
  membershipPlan:MembershipPlanModel,
  children?:React$Node,
  header?:?Object,
  banner:?Object,
  category?:CategoryModel,
  categories:CategoriesCollection,
  customer:CustomerModel,
  userCountry?:string,
  stockReminders?:List<string>,
  memberContent?:Object,
  hideCatalog?:boolean,
  isOffice?:boolean,
};

type State = {
  containerElementId:string,
};

function _getCategoriesSlider(props:Props) {
  const activeUrlKey = props.category ? props.category.getUrlKey() : 'm-all-products';
  const cls = [
    props.hideCatalog ? 'top-margin' : 'remove-top-border',
    'categories-slider-catalog-page',
  ].join(' ');

  return (
    <CategoriesSlider
      className={cls}
      categories={props.categories.getSliderCategories()}
      activeUrlKey={activeUrlKey}
      isMember={props.isMember}
      isOffice={props.isOffice}
    />
  );
}

function _getMemberDiscountInfo(props:Props) {
  if (props.isMember || props.membershipPlan.hideMembershipInfo() || props.isOffice) return null;

  return (
    <section className="member-discount-info hide-sm">
      <div className="discount-info-container">
        <div className="title accent-1 text-2">Our Promise to You</div>
        <span className="text-4">{props.membershipPlan.getDescription() || ''}</span>
        <MsrpLearnMoreLink/>
      </div>
    </section>
  );
}

function _getTestimonials() {
  return (
    <section className="testimonials-container">
      <DelightedTestimonials />
    </section>
  );
}

function _getCatalog(props:Props, state:State) {
  if (props.hideCatalog) return null;
  return (
    <div className="catalog-content">
      <section className="category-content" id={state.containerElementId}>
        <div className="catalog-content-header group">
          {props.header}
          <MediaQuery query={Query.toEm({ min: 641 })}>
            <section className="filter-pills-section">
              <FilterPills selectedFilters={props.filters} />
            </section>
          </MediaQuery>
        </div>
        <section className="small-12 large-3 columns filters-section">
          <MediaQuery query={Query.toEm({ min: 641 })}>
            <FilterDropdown
              filters={props.allFilters}
              selectedFilters={props.filters}
            />
          </MediaQuery>
          {_getMemberDiscountInfo(props)}
          {_getTestimonials()}
        </section>
        <section className="small-12 large-9 columns catalog-content">
          {props.children}
        </section>
      </section>
    </div>
  );
}

function getOffsetY() {
  const element = document.getElementById('category-content');
  if (!element) return 0;
  const rect = element.getBoundingClientRect();
  const scrollY = window.scrollY || 0;
  const y = rect.top + scrollY;
  // Account for header height differences
  // between mobile and desktop
  const screenWidth = window.screen.width;
  const isMobile = screenWidth < 641;
  return isMobile ? y - 60 : y - 150;
}

const hasUrlChanged = (location, prevLocation) => {
  const {pathname, search, hash} = location;
  if (pathname !== prevLocation.pathname || search !== prevLocation.search || hash !== prevLocation.hash) {
    return true;
  }
  return false;
};

class CategoryFrame extends React.Component<Props, State> {

  state = {
    containerElementId: '',
  }

  componentDidMount() {
    // Provide element id on mount instead of before
    // mount so that data is loaded before scroll happens
    // (browser default behavior is to scroll immediately to
    // hash links/elements, which will yield incorrect
    // position if category / other data not yet loaded)
    setTimeout(() => {
      this.setState({containerElementId: 'category-content'});
    }, 200);
    setTimeout(this._scrollIfNecessary, 1300);
  }

  componentDidUpdate(prevProps:Props) {
    const {location} = this.props;
    const prevLocation = prevProps.location;
    const urlChanged = hasUrlChanged(location, prevLocation);
    if (urlChanged) {
      this._scrollIfNecessary();
    }
  }

  _scrollIfNecessary = () => {
    if (UrlData.getHash() === '#category-content') {
      window.scrollTo({
        top: getOffsetY(),
        behavior: 'smooth',
      });
    }
  }

  render() {
    const {props, state} = this;
    return (
      <div className="category-container">
        <div className="category-wrapper group">
          {props.banner}
          {props.memberContent}
          {_getCategoriesSlider(props)}
          {_getCatalog(props, state)}
        </div>
      </div>
    );
  }
}

export default withRouter(CategoryFrame);
